import { useState } from "react";
import type { EvidenciaMini } from "@/lib/api";
import VisorArchivo from "@/ui/VisorArchivo";

type Props = { fotos: EvidenciaMini[] };

/**
 * Tira de fotos de constancia de una nota de la bitácora.
 *
 * Abre el MISMO visor que la galería de Evidencias (carrusel con flechas,
 * teclado y swipe). El carrusel abarca solo las fotos de ESTA nota: es lo
 * predecible cuando entras por la bitácora.
 *
 * Las purgadas por retención (18 meses) no se abren —el servidor responde 410—
 * así que se pintan como marca de que ahí hubo algo, sin dar clic al vacío.
 * Los nombres van en un renglón chico debajo, en el mismo orden (antes vivían
 * en tooltips, invisibles en el celular).
 */
export default function FotosNota({ fotos }: Props) {
  const [abierta, setAbierta] = useState<number | null>(null);
  if (fotos.length === 0) return null;

  const servibles = fotos.filter((f) => f.archivo_eliminado_at === null);

  return (
    <>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {fotos.map((f) =>
          f.archivo_eliminado_at !== null ? (
            <span
              key={f.id}
              className="h-16 w-16 rounded border border-zinc-700 bg-zinc-800/60 text-[9px] text-zinc-500 flex items-center justify-center text-center px-1"
            >
              archivo eliminado
            </span>
          ) : (
            <button
              key={f.id}
              type="button"
              onClick={() => setAbierta(f.id)}
              aria-label={`Ver ${f.nombre_original}`}
              className="h-16 w-16 rounded border border-zinc-700 overflow-hidden hover:border-accent-500 transition focus:outline-none focus:ring-1 focus:ring-accent-500"
            >
              <img
                src={`/api/evidencias/${f.id}/file`}
                alt={f.nombre_original}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </button>
          ),
        )}
      </div>
      <p className="mt-0.5 text-[10px] text-zinc-500 break-words [overflow-wrap:anywhere]">
        {fotos.map((f) => f.nombre_original).join(" · ")}
      </p>

      {abierta !== null && servibles.length > 0 && (
        <VisorArchivo
          targets={servibles.map((f) => ({
            nombre: f.nombre_original,
            mime: f.mime,
            url: `/api/evidencias/${f.id}/file`,
          }))}
          inicial={Math.max(
            0,
            servibles.findIndex((f) => f.id === abierta),
          )}
          onClose={() => setAbierta(null)}
        />
      )}
    </>
  );
}
